// SPDX-License-Identifier: AGPL-3.0-or-later
// Based on au-births by Ben Richardson — https://benrichardson.dev
// Pure analysis: derived series and auto-detected insight cards. No DOM.
import { type BirthsData, type Meta, REGION_ORDER, lastVal, firstVal, exNuptialShare, fmt, fmtInt } from './data';

export type Severity = 'alert' | 'warn' | 'info';
export interface Insight { severity: Severity; title: string; detail: string }

const yearIdx = (data: BirthsData, year: number) => data.years.indexOf(year);

/** Latest non-null value of an AUS summary field. */
function ausLatest(data: BirthsData, key: keyof BirthsData['summary']['AUS']): number | null {
  return lastVal(data.summary.AUS[key]);
}

export function buildInsights(data: BirthsData, meta: Meta): Insight[] {
  const out: Insight[] = [];
  const aus = data.summary.AUS;
  const latestYear = data.years[data.years.length - 1];

  // Record-low TFR.
  if (Math.abs((meta.tfr.latest ?? 0) - (meta.tfr.min ?? 0)) < 1e-9) {
    out.push({
      severity: 'alert',
      title: `Fertility rate at a record low (${fmt(meta.tfr.latest, 3)})`,
      detail: `In ${latestYear} the total fertility rate fell to ${fmt(meta.tfr.latest, 3)} babies per woman — the lowest in the 50-year record and well below the 2.1 needed to replace the population without migration.`,
    });
  } else {
    out.push({
      severity: 'warn',
      title: `Fertility rate ${fmt(meta.tfr.latest, 3)}, near the record low`,
      detail: `The ${latestYear} total fertility rate of ${fmt(meta.tfr.latest, 3)} sits below replacement (2.1); the record low was ${fmt(meta.tfr.min, 3)} in ${meta.tfr.minYear}.`,
    });
  }

  // Below replacement streak.
  if (meta.tfr.belowSince) {
    out.push({
      severity: 'info',
      title: `Below replacement for ${latestYear - meta.tfr.belowSince} years`,
      detail: `Australia's fertility rate first dropped below the 2.1 replacement level in ${meta.tfr.belowSince} and has stayed there every year since — the population's natural growth has depended on migration ever since.`,
    });
  }

  // Median age of mother.
  const dAge = (meta.medianAgeMother.latest ?? 0) - (meta.medianAgeMother.first ?? 0);
  out.push({
    severity: 'info',
    title: `Mothers are ${fmt(dAge, 1)} years older than in ${meta.dataFrom}`,
    detail: `The median age of mothers rose from ${fmt(meta.medianAgeMother.first, 1)} in ${meta.dataFrom} to ${fmt(meta.medianAgeMother.latest, 1)} in ${latestYear}. First-time mothers now have their first child at a median age of ${fmt(ausLatest(data, 'medFirstMother'), 1)}.`,
  });

  // Teen fertility collapse.
  const teenFirst = firstVal(data.asfr.AUS.A15);
  const teenLast = lastVal(data.asfr.AUS.A15);
  if (teenFirst != null && teenLast != null && teenFirst > 0) {
    const drop = (1 - teenLast / teenFirst) * 100;
    out.push({
      severity: 'info',
      title: `Teen births have collapsed by ${Math.round(drop)}%`,
      detail: `The fertility rate for 15–19-year-olds fell from ${fmt(teenFirst, 1)} births per 1,000 women in ${meta.dataFrom} to just ${fmt(teenLast, 1)} in ${latestYear} — one of the sharpest declines of any age group.`,
    });
  }

  // Late-30s overtaking early-20s.
  const a35 = lastVal(data.asfr.AUS.A35);
  const a20 = lastVal(data.asfr.AUS.A20);
  if (a35 != null && a20 != null && a35 > a20) {
    out.push({
      severity: 'info',
      title: 'Women in their late 30s now out-birth women in their early 20s',
      detail: `In ${latestYear} the fertility rate for 35–39-year-olds (${fmt(a35, 1)} per 1,000) overtook that of 20–24-year-olds (${fmt(a20, 1)}) — a reversal of the pattern that held for most of the 20th century.`,
    });
  }

  // Ex-nuptial share.
  const exShare = exNuptialShare(aus);
  const exFirst = firstVal(exShare); const exLast = lastVal(exShare);
  if (exFirst != null && exLast != null) {
    out.push({
      severity: 'info',
      title: `${Math.round(exLast)}% of babies are now born outside marriage`,
      detail: `Births to unmarried parents rose from ${fmt(exFirst, 1)}% of all births in ${meta.dataFrom} to ${fmt(exLast, 1)}% in ${latestYear}. Most are still registered by both parents.`,
    });
  }

  // Highest vs lowest state TFR.
  const stateTfr = REGION_ORDER.map((c) => ({ c, v: lastVal(data.summary[c].tfr) })).filter((x) => x.v != null) as { c: string; v: number }[];
  if (stateTfr.length) {
    stateTfr.sort((a, b) => b.v - a.v);
    const hi = stateTfr[0]; const lo = stateTfr[stateTfr.length - 1];
    out.push({
      severity: 'info',
      title: `${data.regionNames[hi.c]} has the highest fertility, ${data.regionNames[lo.c]} the lowest`,
      detail: `In ${latestYear} ${data.regionNames[hi.c]}'s fertility rate (${fmt(hi.v, 3)}) was the nation's highest and ${data.regionNames[lo.c]}'s (${fmt(lo.v, 3)}) the lowest — a gap of ${fmt(hi.v - lo.v, 2)} babies per woman across the country.`,
    });
  }

  // Births peak vs latest.
  if (meta.births.peakYear !== latestYear) {
    out.push({
      severity: 'info',
      title: `Most babies ever were born in ${meta.births.peakYear}`,
      detail: `${fmtInt(meta.births.peak)} births were registered in ${meta.births.peakYear}, the record. In ${latestYear} there were ${fmtInt(meta.births.latest)} — even as the fertility rate hit a record low, because the population has grown.`,
    });
  }

  // COVID dip + rebound.
  const i2019 = yearIdx(data, 2019); const i2020 = yearIdx(data, 2020); const i2021 = yearIdx(data, 2021);
  if (i2019 >= 0 && i2020 >= 0 && i2021 >= 0) {
    const t19 = aus.tfr[i2019]; const t20 = aus.tfr[i2020]; const t21 = aus.tfr[i2021];
    if (t19 != null && t20 != null && t21 != null && t20 < t19 && t21 > t20) {
      out.push({
        severity: 'info',
        title: 'The pandemic caused a dip, then a mini baby bump',
        detail: `The fertility rate dropped from ${fmt(t19, 3)} (2019) to ${fmt(t20, 3)} in 2020 as couples delayed plans, then rebounded to ${fmt(t21, 3)} in 2021 before resuming its decline.`,
      });
    }
  }

  return out;
}
